import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'

interface ErrorFallbackProps {
  error: Error | unknown
  resetError: () => void
}

function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const { t } = useTranslation()

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-6 text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
          <svg
            className="w-8 h-8 text-red-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.349 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>

        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('common.error')}</h2>

        <p className="text-gray-600 mb-6">{t('errors.somethingWentWrong')}</p>

        <div className="space-y-3">
          <button
            type="button"
            onClick={resetError}
            className="w-full bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary/90 transition-colors"
          >
            {t('errors.tryAgain')}
          </button>

          <button
            type="button"
            onClick={() => {
              window.location.href = '/'
            }}
            className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {t('errors.goBack')}
          </button>
        </div>

        {import.meta.env.DEV && error instanceof Error && (
          <details className="mt-6 text-left">
            <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
              {t('errors.technicalDetails')} ({t('errors.devOnly')})
            </summary>
            <pre className="mt-2 text-xs bg-gray-100 p-3 rounded overflow-auto">{error.stack}</pre>
          </details>
        )}
      </div>
    </div>
  )
}

// Create the Sentry Error Boundary
export const ErrorBoundary = Sentry.withErrorBoundary(
  ({ children }: { children: React.ReactNode }) => <>{children}</>,
  {
    fallback: ErrorFallback,
    beforeCapture: (scope, _error, errorInfo) => {
      scope.setTag('component', 'ErrorBoundary')
      scope.setContext('errorInfo', errorInfo as unknown as Sentry.Context)
    },
  }
)
